import isEqual from 'lodash/isEqual';
import last from 'lodash/last';

import {AdditionalContext, QueryContext} from 'common/query-context';
import {
  ChartType,
  FUNNELS_MATH_TYPES,
  PRIMITIVE_MATH_TYPES,
  ProjectFeatureFlag,
  REPORT_BASICS_LINKS,
  ReportName,
  ResourceType,
  SHOW_LAST_X_DAYS_FEATURE_FLAGS,
} from 'common/report/constants';
import {AllSectionTypes, FilterSection} from 'common/report/insights/models/section';
// TODO move common/report/util/insights to common/report/insights/util
import {QueryMode} from 'common/report/insights/util/constants';
import {
  convertPropertySelectiontoBookmarkProperty,
  getEntries,
  getShowSection,
} from 'common/report/insights/util/events';
import {getFilterSection, getPropertyFilters, getPropertyFiltersOperator} from 'common/report/insights/util/filters';
import {getSegmentSections, getSelectedSegments} from 'common/report/insights/util/segments';
import {getDateRange, getTimeSection} from 'common/report/insights/util/time';
import {
  _getDefaultInsightsSorting,
  canUseInlineFormula,
  getNumMultiAttributionProps,
  getNumShowClausesWithMultiAttribution,
  getQueryModeHeaderTitle,
  getQueryModeRowTypes,
  getQueryTypeOptions,
  getSortConfig,
  hasOnlyEventOrFormulaMetrics,
  hasValidMetricBookmarkParams,
} from 'common/report/insights/util';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {isEventFilter, isUserFilter} from 'common/widgets/property-filter-menu/models/utils';
import {
  QueryBuilderParams,
  QueryBuilderSelection,
  QueryBuilderUpdateDetail,
} from 'common/query-builder/query-builder/types';
import {
  CountBy,
  isEventEntry,
  isMetricEntry,
  MetricEntry,
  QueryEventEntry,
  QueryMetricEntry,
  QuerySuperEntry,
  RowLabelType,
} from 'common/query-builder/query-entry-section/types';
import {ReportQueryBuilder} from 'common/report/report-manager/query-builder';
import {
  ChartPlotStyle,
  InsightsBookmarkParams,
  InsightsQueryError,
  InsightsQueryResults,
  ReportSectionsMetadata,
} from 'common/report/insights/util/types';
import {BaseTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {LiftType} from 'common/report/util/lift/types';
import {setQueryDataGroup} from 'common/query-builder/query-builder/util/data-groups';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {CustomEventDefinition} from 'common/report/util/saved-entities/types';
import {getFirstSegment, isCohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {BookmarkType} from 'common/report/queries/app-api/bookmark-client';
import {QueryContextFnParams} from 'common/report/report-manager/types';
import {canUseViewN, isPivotTableEnabled} from 'common/components/data-table/utils';
import {
  BEHAVIOR_AGGREGATION,
  BEHAVIOR_FREQUENCY,
  BEHAVIOR_MULTI_ATTRIBUTION,
} from 'common/query-builder/query-block/properties-menu/constants';
import {
  isBehaviorSegmentation,
  isMultiAttributionBehavior,
  isMultiAttributionSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {canUseMultiAttribution} from 'common/widgets/multi-attribution-menu/utils';
import {DefaultMultiAttribution} from 'common/widgets/multi-attribution-menu/constants';
import {DashboardOrigin} from 'common/report/util/bookmark-overrides';
import {addToast} from 'common/components/toast-area/toast-manager';
import {MetricType} from 'common/query-builder/query-block/constants';
import {useSingleReport} from 'common/report/util/feature-sampling';
import {
  getQbOtherFilterOptions,
  getQbOtherGroupOptions as getFunnelsQbOtherGroupOptions,
} from 'common/report/funnels/util';
import {getFormulaDisabledReason, getFormulas, getFormulaSection} from 'common/query-builder/insights-formula/utils';
import {getBehaviorShowClauses, isFormulaShowClause} from 'common/report/insights/models/utils';
import {reporter} from 'common/report/error-reporting';
import {RollingWindowSize} from 'common/util';
import {isSupportedMultiAttributionType} from 'common/query-builder/query-builder/util';

import {INSIGHTS_DEFAULT_EVENT, Report} from '../models/report';
import {compareTimeUnits, XAU_TO_TIME_UNIT} from '../util';
import {BehaviorShowClause} from '../models/show-clause';

export function getReportQueryContext(
  report: Report,
  {
    additionalContext,
    defaultReportState,
    originDashboard,
    chartType,
    dataGroups,
    metricType = MetricType.Event,
  }: {
    defaultReportState: Report;
    originDashboard: DashboardOrigin;
    dataGroups: DataGroup[];
    metricType?: MetricType;
    chartType?: ChartType;
    additionalContext?: AdditionalContext;
  },
) {
  if (isEqual(defaultReportState.toBookmarkData(), report.toBookmarkData())) {
    return null;
  }

  const dateRange = getDateRange(report.sections.time.clauses).getAttributes();
  const dataGroupId = report.sections.getDataGroupId();
  const showSection = report.toBookmarkData().sections.show;

  const events: QueryEventEntry[] = [];
  const entries = getEntries({showClauses: showSection, dataGroups});
  entries.forEach(function (entry) {
    if (isMetricEntry(entry)) {
      // flatten funnels
      if (entry.behavior.type === MetricType.Funnel) {
        events.push(
          ...entry.behavior.behaviors.map(
            (behavior) =>
              ({
                type: ResourceType.Event,
                event: {label: behavior.name, value: behavior.name, custom: false},
                filters: entry.behavior.filters,
                filtersOperator: entry.behavior.filtersOperator,
              } as QueryEventEntry),
          ),
        );
      }
    } else if (isEventEntry(entry)) {
      const insightsOnlyEventValues = [`$top_events`, `$all_events`];
      if (!insightsOnlyEventValues.includes(entry.event.value)) {
        events.push({
          type: entry.type,
          event: entry.event,
          filters: entry.filters.filter((f) => (isEventFilter(f) || isUserFilter(f)) && !isBehaviorSegmentation(f)),
          filtersOperator: entry.filtersOperator,
          ...(entry.multiAttribution ? {multiAttribution: entry.multiAttribution} : {}),
        });
      }
    }
  });

  const comparedDateRange = report.timeComparison;
  const segments = getSelectedSegments(report.sections.group.clauses);
  const filters = getPropertyFilters(report.sections.filter.clauses);
  const extraParams = useSingleReport() ? {metricType, chartType} : {};

  return {
    bookmarkType: BookmarkType.Insights,
    querySamplingEnabled: report.querySamplingEnabled,
    dataGroupId,
    dateRange,
    ...(events.length && {events}),
    comparedDateRange,
    ...(originDashboard && {originDashboard}),
    segments: segments.filter((segment) => !isBehaviorSegmentation(getFirstSegment(segment))),
    filters: filters.filter((filter) => !isBehaviorSegmentation(filter)),
    ...extraParams,
    ...(additionalContext ?? {}),
  };
}

export function getReportFromQueryContext(queryContext: QueryContext, params: QueryContextFnParams) {
  const report = Report.createDefaultReport({
    queryMode: QueryMode.Events,
  });

  if (useSingleReport() && queryContext.chartType) {
    report.overrideChartType(queryContext.chartType);
  }

  if (queryContext.dateRange) {
    report.sections.time = getTimeSection(queryContext.dateRange, {useDefaultTimeUnit: true});
  }

  if (queryContext.events) {
    report.sections = report.sections.replaceSection(
      getShowSection({
        entries: queryContext.events,
        dataGroupId: queryContext.dataGroupId,
      }),
    );
  }

  report.sections.setDataGroupId({dataGroupId: queryContext.dataGroupId, customEvents: params.customEvents});

  if (queryContext.comparedDateRange) {
    report.timeComparison = queryContext.comparedDateRange;
  }

  if (queryContext.segments) {
    const segments = [];
    // TODO query cohorts like a property
    let hasCohortSegment = false;
    queryContext.segments.forEach((segment) => {
      if (isCohortSegment(segment)) {
        if (hasCohortSegment === false) {
          segments.push(segment);
        }
        hasCohortSegment = true;
      } else {
        segments.push(segment);
      }
    });

    getSegmentSections(segments).forEach((section) => {
      report.sections = report.sections.replaceSection(section);
    });
  }

  if (queryContext.filters) {
    report.sections = report.sections.replaceSection(getFilterSection(queryContext.filters, null));
  }

  if (queryContext.querySamplingEnabled) {
    report.querySamplingEnabled = true;
  }

  const numSegmentColumns = (queryContext.segments?.length || 0) + (queryContext.events?.length > 1 ? 1 : 0);
  report.sorting = _getDefaultInsightsSorting({
    numSegmentColumns,
    canUseViewN: canUseViewN(params.project),
    hasPivotTablesEnabled: isPivotTableEnabled(params.project),
  });

  return report;
}

/**
 * Makes sure bookmark sorting state matches the expected sorting state at all times.
 */
function updateReportSorting({
  report,
  hasPivotTablesEnabled,
  canUseViewN,
}: {
  report: Report;
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
}) {
  const updatedReport = report.clone();
  const {sections} = updatedReport;
  const hasDistributionAggregation = sections.show
    .getBehaviorClauses()
    .some((c) => c.measurement.math === PRIMITIVE_MATH_TYPES.HISTOGRAM);
  const headerCount = 1 + sections.group.clauses.length + (hasDistributionAggregation ? 1 : 0);
  updatedReport.sorting = getSortConfig({
    headerCount,
    bookmarkSortConfig: updatedReport.sorting,
    hasPivotTablesEnabled,
    numMetrics: sections.formula.clauses.length || sections.show.clauses.length,
    canUseViewN,
    useDefaultViewNForNewSegments: true,
  });
  return updatedReport;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function updateReportFromQBSelection({
  selection,
  report,
  customEvents,
  hasPivotTablesEnabled = false,
  canUseViewN,
  hasConsolidateMetricsUI: consolidateMetricsMode = false,
}: {
  selection: QueryBuilderUpdateDetail;
  report: Report;
  customEvents: CustomEventDefinition[];
  hasPivotTablesEnabled?: boolean;
  canUseViewN?: boolean;
  hasConsolidateMetricsUI?: boolean;
}): Report {
  const oldSections = report.sections;
  const oldHasValidBehaviorMetric = hasValidMetricBookmarkParams(
    getBehaviorShowClauses(report.toBookmarkData().sections.show),
  );

  report = report.clone();

  // for default data group, qb returns an empty string
  if (selection.dataGroupId !== (report.sections.getDataGroupId() ?? ``)) {
    report.sections.setDataGroupId({dataGroupId: selection.dataGroupId, customEvents});
  }

  const showSection = getShowSection({
    entries: selection.entries as Array<QueryMetricEntry | MetricEntry>,
    dataGroupId: selection.dataGroupId,
  });
  const updatedSections: AllSectionTypes[] = [
    showSection,
    // @ts-ignore - FilterSection.toUrlData seems incompatible with Section.toUrlData
    getFilterSection(selection.filters, selection.filtersOperator),
    getFormulaSection(selection.formulas),
    ...getSegmentSections(selection.groupBy),
  ];

  updatedSections.forEach((updatedSection) => {
    report.sections = report.sections.replaceSection(updatedSection);
  });

  const cohortsChanged = !isEqual(report.sections.cohorts.clauses, oldSections.cohorts.clauses);
  const groupsChanged = !isEqual(report.sections.group.clauses, oldSections.group.clauses);
  const segmentsChanged = cohortsChanged || groupsChanged;
  const newHasValidBehaviorMetric = hasValidMetricBookmarkParams(
    getBehaviorShowClauses(report.toBookmarkData().sections.show),
  );
  const addedBehaviorMetric =
    // When loading Insights, it initially starts with 1 show clause that is incomplete, so when this incomplete metric
    // becomes valid, a metric has been added
    (!oldHasValidBehaviorMetric && newHasValidBehaviorMetric) ||
    report.sections.show.getBehaviorClauses().length > oldSections.show.getBehaviorClauses().length;

  const newNumMultiAttributionProps = getNumMultiAttributionProps(report.sections.group.clauses);
  const oldNumMultiAttributionProps = getNumMultiAttributionProps(oldSections.group.clauses);
  const oldNumMultiAttributionModels = getNumShowClausesWithMultiAttribution(oldSections.show.clauses);
  const newNumMultiAttributionModels = getNumShowClausesWithMultiAttribution(report.sections.show.clauses);

  // Case when first attribution property is added
  if (oldNumMultiAttributionProps === 0 && newNumMultiAttributionProps > 0 && newHasValidBehaviorMetric) {
    let incompatibleCountTypeFound = false;
    // Add attribution model to all metrics when first attribution property is added
    report.sections.show.getBehaviorClauses().forEach((clause) => {
      if (isSupportedMultiAttributionType(clause.measurement.math, DefaultMultiAttribution.type)) {
        clause.measurement.multiAttribution = {...DefaultMultiAttribution};
      } else {
        incompatibleCountTypeFound = true;
      }
    });
    if (incompatibleCountTypeFound) {
      addToast({
        message: `Multi attribution could not be added to some events due to incompatible count types.`,
        ctaHref: window.location.href,
        cta: `Undo`,
      });
    }
  }
  // Case when the last attribution property is removed
  else if (oldNumMultiAttributionProps > 0 && newNumMultiAttributionProps === 0) {
    // Remove attribution models from metrics when all attribution property are removed
    report.sections.show.getBehaviorClauses().forEach((clause) => {
      delete clause.measurement.multiAttribution;
    });
  }
  // Case when the last attribution model is removed with an attribution property
  else if (oldNumMultiAttributionModels > 0 && newNumMultiAttributionModels === 0 && newNumMultiAttributionProps > 0) {
    report.sections.group.clauses.forEach((clause) => {
      if (isMultiAttributionBehavior(clause.behavior)) {
        delete clause.behavior;
      }
    });
  }

  if (newNumMultiAttributionProps > 0 && addedBehaviorMetric) {
    const newShowClause = last(report.sections.show.clauses) as BehaviorShowClause;
    // The added metric might already have multi attribution model (if duplicating existing metric that had multi
    // attribution model).
    if (!newShowClause.measurement.multiAttribution) {
      newShowClause.measurement.multiAttribution = {...DefaultMultiAttribution};
    }
  }

  // disallowed chart types
  const {chartType, plotStyle} = report.displayOptions;
  switch (chartType) {
    case ChartType.Line: {
      if (!consolidateMetricsMode) {
        const isPeopleAndNotTimeSeries =
          report.sections.show.hasPeopleQuery() && !report.sections.group.isPeopleTimeSeries();
        if (isPeopleAndNotTimeSeries) {
          // do not allow line charts for non-timeseries people queries
          report.displayOptions.chartType = ChartType.Bar;
        }
        if (report.sections.show.hasEventsQuery() && report.sections.show.hasPeopleQuery()) {
          // Event + Profile mix together will always result in bar
          report.displayOptions.chartType = ChartType.Bar;
        }
      }
      break;
    }
    case ChartType.Bar: {
      const isCohortClauseWithoutGroups =
        report.sections.group.hasGroupByCohortsClause() && !report.sections.group.hasGroupByPropertyClause();
      if (isCohortClauseWithoutGroups && plotStyle === ChartPlotStyle.Stacked) {
        // do not allow stacked bar for queries with cohorts and no groups
        report.displayOptions.plotStyle = ChartPlotStyle.Standard;
      }
      break;
    }
  }

  // correct time unit if clause type has changed
  const timeClause = report.sections.time.clauses[0];
  if (
    !consolidateMetricsMode &&
    report.isCohortsOverTime() &&
    !report.sections.show.hasPeopleQuery() &&
    chartType !== ChartType.Line
  ) {
    report.displayOptions.chartType = ChartType.Line;
  }

  // TODO why dont we use the same helper that the chart header component uses?
  if (
    (report.isCohortsOverTime() || report.hasBehavioralFilterOrSegmentation()) &&
    [`minute`, `hour`].includes(timeClause.unit)
  ) {
    timeClause.unit = BaseTimeUnit.Day;
  }

  if (report.sections.show.hasXauClauses()) {
    if (chartType !== ChartType.Line) {
      // href to current location will redirect to the current pre-change clean URL
      addToast({
        message: `DAU/MAU/WAU calculations are only supported for line charts.`,
        ctaHref: window.location.href,
        cta: `Undo`,
      });
      report.displayOptions.chartType = ChartType.Line;
      report.displayOptions.plotStyle = ChartPlotStyle.Standard;
    }
    const maxTimeUnit = XAU_TO_TIME_UNIT[report.sections.show.lowestXauTimeUnit()];
    const minTimeUnit = TimeUnit.Day;
    const aboveMax = compareTimeUnits(maxTimeUnit, timeClause.unit) < 0;
    const belowMin = compareTimeUnits(minTimeUnit, timeClause.unit) > 0;
    if (aboveMax) {
      timeClause.unit = maxTimeUnit;
    } else if (belowMin) {
      timeClause.unit = minTimeUnit;
    }
  }

  // If any of the show clause has rolling or cumulative calculation, change chart type to line chart
  if (
    (report.sections.show.hasRollingClauses() || report.sections.show.hasCumulativeClauses()) &&
    chartType !== ChartType.Line
  ) {
    // href to current location will redirect to the current pre-change clean URL
    addToast({
      message: `Rolling or cumulative calculations are only supported for line charts.`,
      ctaHref: window.location.href,
      cta: `Undo`,
    });
    report.displayOptions.chartType = ChartType.Line;
    report.displayOptions.plotStyle = ChartPlotStyle.Standard;
  }

  const liftType = report.liftComparison?.type;
  // If we change breakdowns, we don't want to compare against a baseline value that might not exist, so clear lift
  // We also can't do overall, because there's no segments to compare to overall. Time comparison is fine.
  if ((liftType === LiftType.Segment || liftType === LiftType.Overall) && segmentsChanged) {
    report.liftComparison = null;
  }

  report = updateReportSorting({report, hasPivotTablesEnabled, canUseViewN});

  return report;
}

/**
 * Sanitizes a report for the given data group using the QueryBuilder #setQueryDataGroup util.
 */
export function sanitizeReportForDataGroup({
  report,
  dataGroupId,
  dataGroups,
  customEvents,
  hasConsolidateMetricsUI,
  reportSectionsMetadata,
}: {
  report: Report;
  dataGroupId: string;
  dataGroups: DataGroup[];
  customEvents: CustomEventDefinition[];
  hasConsolidateMetricsUI: boolean;
  reportSectionsMetadata: ReportSectionsMetadata;
}) {
  // convert Report to QueryBuilderSelection
  const query: QueryBuilderSelection = {
    dataGroupId: report.sections.getDataGroupId(),
    dateRange: getDateRange(report.sections.time.clauses).getAttributes(),
    entries: getEntries({
      showClauses: report.toBookmarkData().sections.show,
      dataGroups,
      dateUnit: report.sections.time.clauses[0].unit,
      reportSectionsMetadata,
    }),
    exclusionSteps: [],
    groupBy: getSelectedSegments(report.sections.group.clauses),
    filters: getPropertyFilters(report.sections.filter.clauses),
    filtersOperator: getPropertyFiltersOperator(report.sections.filter.determiner),
    attributionSelection: null,
    formulas: getFormulas(report.sections.formula.clauses),
  };

  // sanitize the QueryBuilderSelection
  const sanitizedQuery = setQueryDataGroup({dataGroupId, query, customEvents});

  // convert the new QueryBuilderSelection to Report
  const sanitizedSelection = {...sanitizedQuery, delta: {}, forceQuery: false};
  return updateReportFromQBSelection({
    selection: sanitizedSelection,
    report,
    customEvents,
    hasConsolidateMetricsUI,
  });
}

type InsightsQueryBuilderState = {
  queryMode: QueryMode;
  reports: {
    [QueryMode.Events]: Report | null;
    [QueryMode.Groups]: Report | null;
  };
};
class InsightsQueryBuilder extends ReportQueryBuilder<
  InsightsBookmarkParams,
  InsightsQueryResults,
  InsightsQueryBuilderState
> {
  displayContext = SearchDisplayContext.Insights;
  reportType = ReportName.Insights;
  _error: Nullable<InsightsQueryError> = null;

  get defaultState() {
    return {
      // The Insights QB keeps around several unsaved report states so that
      // users can flip between different "query modes:" Events(&Cohorts) / People
      // modes and not lose their work. It's an unfortunate hunk of statefulness in
      // the QB which otherwise could depend solely on a bookmark and not need the
      // whole Insights model layer. A UX solution to this would be for "people" and
      // "events" query modes to be top-level analysis types that you could add to a
      // board, rather than adding an "Insights" and then using the QB to choose People
      // or Events/Cohorts.
      queryMode: QueryMode.Events,
      reports: {
        [QueryMode.Events]: null,
        [QueryMode.Groups]: null,
      },
    };
  }

  bookmarkUpdateFromQbUpdate(updateDetail: QueryBuilderUpdateDetail): InsightsBookmarkParams {
    const {delta} = updateDetail;
    if (`queryType` in delta && delta.queryType !== this.state.queryMode) {
      const queryMode = delta.queryType as QueryMode;
      this.update({queryMode});
      if (queryMode === QueryMode.Groups) {
        return this.report.toBookmarkData();
      }
    }

    const report = updateReportFromQBSelection({
      selection: updateDetail,
      report: this.report,
      customEvents: this.params.customEvents,
      hasPivotTablesEnabled: isPivotTableEnabled(this.params.project),
      canUseViewN: canUseViewN(this.params.project),
      hasConsolidateMetricsUI: this.hasConsolidateMetricsUI(),
    });

    return report.toBookmarkData();
  }

  public connectedCallback() {
    super.connectedCallback();
    this.updateReports(this.params.bookmarkParams);
  }

  qbChildrenTemplate() {
    return null;
  }

  get currentReport() {
    return this.state.reports[this.state.queryMode];
  }

  reportSpecificQBParams(): Partial<QueryBuilderParams> {
    if (!this.currentReport) {
      return {};
    }

    const {displayOptions, sections} = this.currentReport;
    const showSection = this.currentReport.toBookmarkData().sections.show;
    const entries = getEntries({
      showClauses: showSection,
      dataGroups: this.params.dataGroups,
      queryError: this.params.error,
      dateUnit: sections.time.clauses[0].unit,
      reportSectionsMetadata: this.params.results?.meta?.report_sections,
    });
    const dataGroupId = sections.getDataGroupId();
    const canUseMultiAttributionVal = canUseMultiAttribution(this.params.project);
    const hasMultiAttributionProp = getNumMultiAttributionProps(sections.group.clauses) > 0;
    const canUseInlineFormulaVal = canUseInlineFormula(this.params.project);

    const groupBy = getSelectedSegments(sections.group.clauses);
    const groupByUndraggableRows = groupBy.reduce((acc, segment, idx) => {
      if (isCohortSegment(segment) || isMultiAttributionSegmentation(getFirstSegment(segment))) {
        acc.push(idx);
      }
      return acc;
    }, [] as number[]);

    const formulasDisableReason = getFormulaDisabledReason(this.currentReport);
    return {
      allowCohortFilter: true,
      disableAddEntry: this.params.insightsReportMode === ReportName.Funnels,
      addEntryButtonLabel: `Add Metric`,
      addEntryButtonTooltipText: `Add an event or cohort to analyze.\n[Read Intro to Insights](${
        REPORT_BASICS_LINKS[ReportName.Insights]
      })`,
      canCustomizeNumericBuckets: true,
      canUseMultiAttribution: canUseMultiAttributionVal && hasMultiAttributionProp,
      chartType: this.currentReport.displayOptions.chartType,
      ...(dataGroupId ? {dataGroupId} : {}),
      dateRange: getDateRange(sections.time.clauses).getAttributes(),
      dateUnit: (sections.time.clauses[0].unit as unknown) as RollingWindowSize,
      disableAddEntryReason:
        this.currentReport.displayOptions.chartType === ChartType.Table && this.currentReport.hasTopEvents()
          ? `Events cannot be added on tables with "Your Top Events".`
          : ``,
      enableEnhancedListFilters: true,
      entryRowLabelType: RowLabelType.Alpha,
      entriesDraggable: true,
      entries,
      minEntries: 1,
      emptyPlaceholder:
        this.state.queryMode === QueryMode.Events
          ? ({...INSIGHTS_DEFAULT_EVENT, dataGroupId} as QuerySuperEntry)
          : null,
      includeCustomProperties: `all`,
      filtersOperator: getPropertyFiltersOperator(sections.filter.determiner || FilterSection.DETERMINER_ALL),
      filters: getPropertyFilters(sections.filter.clauses),
      formulas: getFormulas(sections.formula.clauses),
      formulasDisableReason,
      formulasError: this.params.error,
      groupBy,
      groupByUndraggableRows,
      hideDatePicker: true,
      metricsMode: true,
      otherFilterOptions: this.getOtherFilterOptions(),
      otherGroupByOptions: this.getOtherGroupByOptions(canUseMultiAttributionVal, hasMultiAttributionProp, sections),
      plotStyle: displayOptions.plotStyle,
      queryTypeOptions: getQueryTypeOptions({
        canUseCohorts: this.canUseCohorts(),
        canUseInlineFormula: canUseInlineFormulaVal,
      }),
      rowTypes: getQueryModeRowTypes({
        queryMode: this.state.queryMode,
        canUseCohorts: this.canUseCohorts(),
        canUseInlineFormula: canUseInlineFormulaVal,
        isSingleReport: useSingleReport(),
        hasConsolidateMetricsUI: this.hasConsolidateMetricsUI(),
        formulasDisableReason,
      }),
      selectedQueryType: this.state.queryMode,
      headerTitle: getQueryModeHeaderTitle({
        queryMode: this.state.queryMode,
        canUseCohorts: this.canUseCohorts(),
        canUseInlineFormula: canUseInlineFormulaVal,
      }),
      showEntryRename: true,
      showSpecialDatasetProperty: this.params.project.isRollupProject(),
      /**
       * Hide Top Events from events-menu if table chart has another metric already. Insights table UI for
       * column/metric labeling looks weird when Top Events is mixed with other events
       */
      restrictTopEventsOnly: this.currentReport.displayOptions.chartType === ChartType.Table,
      supportB2bCohorts: this.canUseDataGroups(),
      switchableReportName:
        this.params.insightsReportMode === ReportName.Funnels ? ReportName.Funnels : ReportName.Insights,
      reportName: this.params.insightsReportMode === ReportName.Funnels ? ReportName.Funnels : ReportName.Insights,

      // disable profile history in profiles mode for now. need alignment on whether it's useful
      disableProfileHistory: this.state.queryMode === QueryMode.Groups,
    };
  }

  get report() {
    return this.state.reports[this.state.queryMode];
  }

  updateReport(value) {
    this.state.reports[this.state.queryMode] = value;
    this.update();
  }

  onBookmarkUpdated(newBookmarkParams: InsightsBookmarkParams) {
    this.updateReports(newBookmarkParams);
  }

  private getOtherFilterOptions() {
    return [
      ...(this.canUseBehaviors() ? [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value] : []),
      ...(this.report.sections.show.hasFunnelsQuery() ? getQbOtherFilterOptions() : []),
    ];
  }

  private getOtherGroupByOptions(canUseMultiAttributionVal, hasMultiAttributionProp, sections) {
    return [
      ...(this.canUseBehaviors() ? [BEHAVIOR_FREQUENCY.value, BEHAVIOR_AGGREGATION.value] : []),
      ...(canUseMultiAttributionVal && !hasMultiAttributionProp && hasOnlyEventOrFormulaMetrics(sections.show.clauses)
        ? [BEHAVIOR_MULTI_ATTRIBUTION.value]
        : []),
      ...(this.report.sections.show.hasFunnelsQuery() ? getFunnelsQbOtherGroupOptions(null) : []),
    ];
  }

  // a bunch of stuff horked from insights-app/insights-query/index
  private updateReports(bookmarkParams: InsightsBookmarkParams) {
    const report = Report.fromBookmarkData(bookmarkParams);

    const {reports} = this.state;
    const dataGroupId = report.sections.getDataGroupId();

    let loadedReportMode;
    if (report?.isEmpty()) {
      loadedReportMode = this.state.queryMode;
    } else {
      loadedReportMode = report.sections.show.isPeopleOnlyQuery() ? QueryMode.Groups : QueryMode.Events;
    }

    const updatedReports = {
      [QueryMode.Events]: reports[QueryMode.Events]
        ? reports[QueryMode.Events].clone()
        : this.defaultReportState(
            this.params.insightsReportMode === ReportName.Funnels ? QueryMode.Funnels : QueryMode.Events,
            dataGroupId,
          ),
      [QueryMode.Groups]: reports[QueryMode.Groups]
        ? reports[QueryMode.Groups].clone()
        : this.defaultReportState(QueryMode.Groups, dataGroupId),
      ...{
        [loadedReportMode]: report.clone(),
      },
    };

    // sync data group id to the other report
    const otherQueryMode = loadedReportMode === QueryMode.Events ? QueryMode.Groups : QueryMode.Events;
    const otherReport = updatedReports[otherQueryMode];
    if (dataGroupId !== otherReport.sections.getDataGroupId()) {
      const sanitizedOtherReport = sanitizeReportForDataGroup({
        report: otherReport,
        dataGroupId,
        dataGroups: this.params.dataGroups,
        customEvents: this.params.customEvents,
        hasConsolidateMetricsUI: this.hasConsolidateMetricsUI(),
        reportSectionsMetadata: this.params.results?.meta?.report_sections,
      });
      updatedReports[otherQueryMode] = sanitizedOtherReport;
    }

    this.update({reports: updatedReports, queryMode: loadedReportMode});
  }

  handleFunnelsCountBySelected = (ev: CustomEvent<{item: CountBy}>) => {
    const countBy = ev.detail.item;
    const updatedReport = this.report.clone();

    (updatedReport.sections.show.clauses as BehaviorShowClause[]).forEach((clause) => {
      clause.measurement.math = countBy.aggregationOperator;
      if (countBy?.property) {
        clause.measurement.property = convertPropertySelectiontoBookmarkProperty(countBy.property);
      }
    });

    this.updateReport(updatedReport);

    this.dispatchUpdate({params: updatedReport.toBookmarkData(), queryReason: QueryReason.QbOtherUpdate});
  };

  getFunnelsCountType() {
    const bookmark = this.params.bookmarkParams;
    // TODO(CC): When we start supporting multi metrics, replace 0 to the given index
    // For now, we assume there can be only one metric
    const showClause = bookmark.sections.show[0];
    if (isFormulaShowClause(showClause)) {
      reporter.error(`getFunnelsCountType: unsupported formula show clause`, {showClause});
      return null;
    }

    const math = showClause.measurement.math;
    const property = showClause.property;

    // If the math is not one of FUNNELS_MATH_TYPES, set it to funnels default count type "conversion_rate"
    if (
      !Object.values(FUNNELS_MATH_TYPES).includes(math as FUNNELS_MATH_TYPES) ||
      (math === FUNNELS_MATH_TYPES.PROPERTY_SUM && !property)
    ) {
      return FUNNELS_MATH_TYPES.UNIQUE;
    }
    return math;
  }

  getEventNamesforPropertyMenu() {
    const bookmark = this.params.bookmarkParams;

    const eventNames = bookmark.sections.show.map((clause) => {
      if (isFormulaShowClause(clause)) {
        reporter.error(`getEventNamesforPropertyMenu: unsupported formula show clause`, {clause});
        return null;
      }

      if (clause.behavior.value.custom === true) {
        return `$custom_event:${clause.behavior.value.id}`;
      }

      return clause.behavior.value.name;
    });

    return eventNames;
  }

  private defaultReportState(queryMode = QueryMode.Events, dataGroupId = null) {
    const showLastXDaysFlag = SHOW_LAST_X_DAYS_FEATURE_FLAGS.filter((flag: ProjectFeatureFlag) =>
      this.params.project?.hasFeatureFlag(flag),
    );

    const result = Report.createDefaultReport({
      dataGroupId,
      queryMode: queryMode === QueryMode.Groups || this.shouldDefaultToPeopleMode() ? QueryMode.Groups : queryMode,
      showLastXDays: showLastXDaysFlag ? showLastXDaysFlag[0] : null,
    });
    return result;
  }

  private shouldDefaultToPeopleMode() {
    return !!this.params.project && this.params.project.isPeopleOnlyProject();
  }

  canUseBehaviors() {
    return this.state.queryMode !== QueryMode.Groups;
  }
}

export const InsightsQueryBuilderEl = defineAndMakeJsxFactory(`insights-query-builder`, InsightsQueryBuilder);
export const InsightsQueryBuilderReact = panel2react(`insights-query-builder`, InsightsQueryBuilder);
